import React, { useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  SvgAdd,
  SvgCheveronLeft,
  SvgCheveronRight,
} from '@actual-app/components/icons/v1';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { addDays } from '@actual-app/core/shared/mtxbudget';
import {
  calendarEvents,
  dayTotals,
  monthTotals,
  monthWeeks,
  weekOf,
} from '@actual-app/core/shared/mtxbudget-calendrier';
import type { CalendarEvent } from '@actual-app/core/shared/mtxbudget-calendrier';
import { q } from '@actual-app/core/shared/query';

import { useNavigate } from '#hooks/useNavigate';
import { useQuery } from '#hooks/useQuery';

import { hoursLabel, longDate, MtxPage, Section, useMoney } from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useParcours } from './useParcours';

type Row = {
  id: string;
  date: string;
  amount: number;
  payee_name: string | null;
  account_name: string | null;
  notes: string | null;
  transfer_id: string | null;
  starting_balance_flag: boolean;
};

const monthYear = new Intl.DateTimeFormat('fr-CA', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const weekday = new Intl.DateTimeFormat('fr-CA', {
  weekday: 'short',
  timeZone: 'UTC',
});
const rounded = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 0 });
const monthOnly = new Intl.DateTimeFormat('fr-CA', {
  month: 'long',
  timeZone: 'UTC',
});
const dayShortMonth = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const dayMonthYear = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

type Mode = 'week' | 'month' | 'year' | 'list';
const MODES: Mode[] = ['week', 'month', 'year', 'list'];

function utc(date: string) {
  return new Date(`${date}T00:00:00Z`);
}

/** « 11 – 17 octobre 2026 », or « 27 sept. – 3 oct. 2026 » across two months. */
function weekTitle(week: string[]) {
  const [first, last] = [week[0], week[6]];
  return first.slice(0, 7) === last.slice(0, 7)
    ? `${Number(first.slice(8))} – ${dayMonthYear.format(utc(last))}`
    : `${dayShortMonth.format(utc(first))} – ${dayShortMonth.format(utc(last))} ${last.slice(0, 4)}`;
}

function capital(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function shiftMonth(month: string, by: number) {
  const [year, index] = month.split('-').map(Number);
  return new Date(Date.UTC(year, index - 1 + by, 1)).toISOString().slice(0, 7);
}

function tint(color: string) {
  return `color-mix(in srgb, ${color} 30%, transparent)`;
}

function chipStyle(isOn: boolean): CSSProperties {
  return {
    minHeight: 44,
    padding: '0 14px',
    borderRadius: 22,
    border: `1px solid ${isOn ? theme.buttonPrimaryBackground : theme.tableBorder}`,
    backgroundColor: isOn
      ? theme.buttonPrimaryBackground
      : theme.tableBackground,
    color: isOn ? theme.buttonPrimaryText : theme.pageText,
    font: 'inherit',
    fontSize: 13,
    fontWeight: isOn ? 600 : 400,
    cursor: 'pointer',
  };
}

/**
 * Money in and money out, laid on a calendar: up to today what went through
 * the bank, from today on what the walk through the pays plans. A month grid
 * with the events of the day chosen, or the list of the days of the month.
 */
export function CalendrierPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const { config, isLoading } = useMtxbudget();
  const { steps, status } = useParcours();
  const today = monthUtils.currentDay();

  const [view, setView] = useState<Mode>('month');
  // The day chosen: it sets the week, the month and the year on screen.
  const [selected, setSelected] = useState(today);
  const month = selected.slice(0, 7);
  const year = selected.slice(0, 4);

  const weeks = monthWeeks(month);
  const week = weekOf(selected);
  const [from, to] =
    view === 'week'
      ? [week[0], week[6]]
      : view === 'year'
        ? [`${year}-01-01`, `${year}-12-31`]
        : [weeks[0][0], weeks.at(-1)?.at(-1) ?? weeks[0][0]];
  const { data: rows } = useQuery<Row>(
    () =>
      q('transactions')
        .filter({ date: { $gte: from, $lte: to } })
        .select([
          'id',
          'date',
          'amount',
          { payee_name: 'payee.name' },
          { account_name: 'account.name' },
          'notes',
          'transfer_id',
          'starting_balance_flag',
        ]),
    [from, to],
  );

  const events = calendarEvents({
    today,
    steps,
    transactions: (rows ?? []).map(row => ({
      id: row.id,
      date: row.date,
      amount: row.amount,
      name: row.payee_name || row.notes || t('Transaction'),
      account: row.account_name ?? undefined,
      isTransfer: row.transfer_id != null,
      isStartingBalance: row.starting_balance_flag,
    })),
  }).filter(event => event.date >= from && event.date <= to);
  const totals = dayTotals(events);
  const paydays = new Set(steps.map(step => step.payday));
  // The walk plans thirteen pays: beyond, nothing is planned yet.
  const horizon = steps.at(-1)?.nextPayday ?? today;
  const inColor = theme.noticeTextLight;
  const outColor = theme.mobileNavItemSelected;

  const inMonth = (date: string) => date.startsWith(month);
  // What the title sums up: the week, the month or the year on screen.
  const inPeriod = (date: string) =>
    view === 'week'
      ? date >= week[0] && date <= week[6]
      : view === 'year'
        ? date.startsWith(year)
        : inMonth(date);
  const periodIn = [...totals].reduce(
    (sum, [date, total]) => sum + (inPeriod(date) ? total.in : 0),
    0,
  );
  const periodOut = [...totals].reduce(
    (sum, [date, total]) => sum + (inPeriod(date) ? total.out : 0),
    0,
  );

  /** The month holding today opens on today, any other on its first day. */
  function openMonth(next: string) {
    setSelected(next === today.slice(0, 7) ? today : `${next}-01`);
  }

  function step(by: 1 | -1) {
    if (view === 'week') {
      setSelected(addDays(selected, 7 * by));
    } else if (view === 'year') {
      const next = String(Number(year) + by);
      setSelected(next === today.slice(0, 4) ? today : `${next}-01-01`);
    } else {
      openMonth(shiftMonth(month, by));
    }
  }

  function nameOf(event: CalendarEvent) {
    return event.kind === 'pay'
      ? t('Paie')
      : event.kind === 'debt'
        ? t('Versement à {{name}}', { name: event.name })
        : event.name;
  }

  function detailOf(event: CalendarEvent) {
    switch (event.kind) {
      case 'transaction':
        return event.account
          ? t('Passé à la banque · {{account}}', { account: event.account })
          : t('Passé à la banque');
      case 'pay':
        return t('Salaire net prévu · {{first}} et {{second}}', {
          first: hoursLabel(event.hours?.[0] ?? 0),
          second: hoursLabel(event.hours?.[1] ?? 0),
        });
      case 'debt':
        return t('Surplus de la paie, décidé par le calcul');
      case 'income':
        return t("Entrée d'argent prévue · modifier");
      case 'oneoff':
        return t('Dépense ponctuelle prévue · modifier');
      case 'investment':
        return t('Investissement financé avant les dettes · modifier');
      default:
        return event.skipped
          ? t('Échéance désactivée : ne sera pas payée')
          : event.card
            ? t('Échéance · payée par {{card}}', { card: event.card })
            : t('Échéance prévue');
    }
  }

  function amountOf(event: CalendarEvent) {
    return `${event.amount > 0 ? '+' : '−'} ${money(Math.abs(event.amount))}`;
  }

  function edit(event: CalendarEvent) {
    if (event.edit) {
      void navigate(
        `/previsions?modifier=${event.edit.kind}:${encodeURIComponent(event.edit.id)}`,
      );
    }
  }

  function net(date: string) {
    const total = totals.get(date);
    return total ? total.in - total.out : 0;
  }

  if (!config) {
    return (
      <MtxPage title={t('Calendrier')}>
        {!isLoading && (
          <Section style={{ padding: 16 }}>
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>MTXbudget n'est pas encore configuré.</Trans>
            </Text>
          </Section>
        )}
      </MtxPage>
    );
  }

  const beyond =
    (view === 'week'
      ? week[0]
      : view === 'year'
        ? `${year}-01-01`
        : `${month}-01`) >= horizon;
  const note =
    status === 'loading'
      ? t('Calcul des prévisions en cours…')
      : beyond
        ? t(
            'Au-delà des six prochains mois : les prévisions ne sont pas encore calculées.',
          )
        : null;
  const dayEvents = events.filter(event => event.date === selected);
  const listDays = [
    ...new Set([
      ...events.filter(event => inMonth(event.date)).map(event => event.date),
      ...(inMonth(today) ? [today] : []),
    ]),
  ].sort();

  const row = (event: CalendarEvent, card: boolean, last: boolean) => {
    const color = event.amount > 0 ? inColor : outColor;
    const style: CSSProperties = card
      ? {
          minHeight: 44,
          padding: '4px 12px',
          borderRadius: 4,
          border: `1px solid ${event.done ? 'transparent' : color}`,
          backgroundColor: event.done ? tint(color) : 'transparent',
        }
      : {
          padding: '10px 16px',
          border: 0,
          borderBottom: last ? undefined : `1px solid ${theme.tableBorder}`,
          background: 'none',
        };
    const content = (
      <>
        {!card && (
          <span
            style={{
              width: 4,
              alignSelf: 'stretch',
              borderRadius: 2,
              backgroundColor: color,
              opacity: event.done ? 1 : 0.55,
            }}
          />
        )}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <span
            style={{
              fontWeight: 500,
              color: theme.pageTextDark,
              textDecoration: event.skipped ? 'line-through' : undefined,
            }}
          >
            {nameOf(event)}
          </span>
          <span style={{ fontSize: 12, color: theme.pageTextSubdued }}>
            {detailOf(event)}
          </span>
        </span>
        <span
          style={{
            flexShrink: 0,
            fontWeight: 500,
            color: event.amount > 0 ? inColor : theme.pageText,
            textDecoration: event.skipped ? 'line-through' : undefined,
          }}
        >
          {amountOf(event)}
        </span>
      </>
    );
    const shared: CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: card ? 8 : 12,
      color: theme.pageText,
      font: 'inherit',
      textAlign: 'left',
      ...style,
    };
    return event.edit ? (
      <button
        key={event.id}
        type="button"
        aria-label={t('Modifier : {{name}}', { name: nameOf(event) })}
        onClick={() => edit(event)}
        style={{ ...shared, cursor: 'pointer' }}
      >
        {content}
      </button>
    ) : (
      <div key={event.id} style={shared}>
        {content}
      </div>
    );
  };

  const monthGrid = (
    <>
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          margin: '0 -6px',
          fontSize: 11,
          color: theme.pageTextSubdued,
          textAlign: 'center',
        }}
      >
        {weeks[0].map(date => (
          <span key={date}>
            {weekday.format(new Date(`${date}T00:00:00Z`))}
          </span>
        ))}
      </View>
      <View
        role="grid"
        aria-label={t('Jours du mois')}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          margin: '0 -6px',
          borderBottom: `1px solid ${theme.tableBorder}`,
        }}
      >
        {weeks.flat().map(date => {
          const total = totals.get(date);
          const isSelected = date === selected;
          const done = date < today;
          const amount = (value: number, color: string, sign: string) => (
            <span
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: done ? '1px 2px' : '0 2px',
                borderRadius: 3,
                border: done ? 0 : `1px solid ${color}`,
                backgroundColor: done ? tint(color) : 'transparent',
                color: theme.pageTextDark,
                fontSize: 10,
                fontWeight: 400,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {`${sign}${rounded.format(value / 100)}`}
            </span>
          );
          return (
            <button
              key={date}
              type="button"
              aria-pressed={isSelected}
              aria-label={capital(longDate(date))}
              onClick={() => setSelected(date)}
              style={{
                height: 70,
                padding: '4px 2px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                border: isSelected
                  ? `1px solid ${outColor}`
                  : '1px solid transparent',
                borderTop: isSelected
                  ? `1px solid ${outColor}`
                  : `1px solid ${theme.tableBorder}`,
                borderRadius: isSelected ? 4 : 0,
                backgroundColor: isSelected
                  ? theme.tableHeaderBackground
                  : 'transparent',
                color: inMonth(date) ? theme.pageText : theme.pageTextSubdued,
                opacity: inMonth(date) ? 1 : 0.55,
                font: 'inherit',
                fontSize: 12,
                fontWeight: isSelected ? 700 : 400,
                cursor: 'pointer',
              }}
            >
              {date === today ? (
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    backgroundColor: theme.pageTextDark,
                    color: theme.pageBackground,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {Number(date.slice(8))}
                </span>
              ) : (
                Number(date.slice(8))
              )}
              {total && total.in > 0 && amount(total.in, inColor, '+')}
              {total && total.out > 0 && amount(total.out, outColor, '−')}
            </button>
          );
        })}
      </View>
    </>
  );

  const legend = (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        padding: '6px 4px',
        fontSize: 11,
        color: theme.pageTextSubdued,
      }}
    >
      <span>
        <span
          style={{
            display: 'inline-block',
            width: 18,
            height: 10,
            borderRadius: 3,
            backgroundColor: tint(inColor),
            verticalAlign: 'middle',
          }}
        />{' '}
        <Trans>entrée</Trans>
      </span>
      <span>
        <span
          style={{
            display: 'inline-block',
            width: 18,
            height: 10,
            borderRadius: 3,
            backgroundColor: tint(outColor),
            verticalAlign: 'middle',
          }}
        />{' '}
        <Trans>sortie</Trans>
      </span>
      <span>
        <Trans>plein : passé à la banque · contour : prévu</Trans>
      </span>
    </View>
  );

  const dayPanel = (
    <>
      <Section
        title={`${capital(longDate(selected))}${paydays.has(selected) ? ` · ${t('jour de paie')}` : ''}`}
        aside={
          dayEvents.length > 0 ? (
            <span
              style={{
                color: net(selected) >= 0 ? inColor : theme.pageText,
              }}
            >
              {`${net(selected) >= 0 ? '+' : '−'} ${money(Math.abs(net(selected)))}`}
            </span>
          ) : undefined
        }
      >
        {dayEvents.length === 0 ? (
          <Text
            style={{
              padding: 16,
              fontSize: 13,
              color: theme.pageTextSubdued,
            }}
          >
            {selected < today
              ? t("Rien n'est passé à la banque ce jour-là.")
              : t('Rien de prévu ce jour-là.')}
          </Text>
        ) : (
          dayEvents.map((event, index) =>
            row(event, false, index === dayEvents.length - 1),
          )
        )}
      </Section>
      {selected >= today && (
        <button
          type="button"
          onClick={() =>
            void navigate(`/previsions?ajouter=depense&date=${selected}`)
          }
          style={{
            marginTop: 8,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            borderRadius: 4,
            border: `1px solid ${theme.tableBorder}`,
            backgroundColor: theme.tableBackground,
            color: theme.pageText,
            font: 'inherit',
            fontSize: 15,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <SvgAdd width={16} height={16} />
          {t('Ajouter une prévision le {{date}}', {
            date: longDate(selected).replace(/^\S+ /, ''),
          })}
        </button>
      )}
    </>
  );

  const list =
    listDays.length === 0 ? (
      <Section style={{ padding: 16 }}>
        <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
          <Trans>Aucune entrée ni sortie d'argent ce mois-ci.</Trans>
        </Text>
      </Section>
    ) : (
      listDays.map((date, index) => {
        const list = events.filter(event => event.date === date);
        return (
          <View
            key={date}
            style={{
              flexDirection: 'row',
              gap: 12,
              padding: '8px 4px',
              borderTop:
                index === 0 ? undefined : `1px solid ${theme.tableBorder}`,
            }}
          >
            <View style={{ width: 44, flexShrink: 0, alignItems: 'center' }}>
              <Text style={{ fontSize: 11, color: theme.pageTextSubdued }}>
                {weekday.format(new Date(`${date}T00:00:00Z`))}
              </Text>
              {date === today ? (
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: theme.pageTextDark,
                    color: theme.pageBackground,
                    fontSize: 17,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {Number(date.slice(8))}
                </span>
              ) : (
                <Text style={{ fontSize: 20, fontWeight: 600 }}>
                  {Number(date.slice(8))}
                </Text>
              )}
            </View>
            <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
              {list.length === 0 ? (
                <Text
                  style={{
                    minHeight: 44,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 13,
                    color: theme.pageTextSubdued,
                  }}
                >
                  <Trans>Aujourd'hui · rien de prévu</Trans>
                </Text>
              ) : (
                <>
                  {paydays.has(date) && date >= today && (
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      {t('Jour de paie · {{amount}} sur la journée', {
                        amount: `${net(date) >= 0 ? '+' : '−'} ${money(Math.abs(net(date)))}`,
                      })}
                    </Text>
                  )}
                  {list.map(event => row(event, true, false))}
                </>
              )}
            </View>
          </View>
        );
      })
    );

  const block = (event: CalendarEvent) => {
    const color = event.amount > 0 ? inColor : outColor;
    return (
      <span
        key={event.id}
        style={{
          padding: event.done ? '3px 2px' : 2,
          borderRadius: 3,
          border: event.done ? 0 : `1px solid ${color}`,
          backgroundColor: event.done ? tint(color) : 'transparent',
          color: theme.pageTextDark,
          fontSize: 10,
          lineHeight: 1.25,
          fontWeight: 400,
          textAlign: 'left',
          overflow: 'hidden',
          opacity: event.skipped ? 0.55 : 1,
          textDecoration: event.skipped ? 'line-through' : undefined,
        }}
      >
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {nameOf(event)}
        </span>
        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
          {`${event.amount > 0 ? '+' : '−'}${rounded.format(Math.abs(event.amount) / 100)}`}
        </span>
      </span>
    );
  };

  const weekGrid = (
    <View
      role="grid"
      aria-label={t('Jours de la semaine')}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 2,
        margin: '0 -6px',
        borderBottom: `1px solid ${theme.tableBorder}`,
      }}
    >
      {week.map(date => {
        const isSelected = date === selected;
        return (
          <button
            key={date}
            type="button"
            aria-pressed={isSelected}
            aria-label={capital(longDate(date))}
            onClick={() => setSelected(date)}
            style={{
              minHeight: 230,
              minWidth: 0,
              padding: '4px 1px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 3,
              border: `1px solid ${isSelected ? outColor : 'transparent'}`,
              borderRadius: 4,
              backgroundColor: isSelected
                ? theme.tableHeaderBackground
                : 'transparent',
              color: theme.pageText,
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: theme.pageTextSubdued,
                textAlign: 'center',
              }}
            >
              {weekday.format(utc(date))}
            </span>
            {date === today ? (
              <span
                style={{
                  alignSelf: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: theme.pageTextDark,
                  color: theme.pageBackground,
                  fontSize: 13,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {Number(date.slice(8))}
              </span>
            ) : (
              <span
                style={{
                  fontSize: 15,
                  fontWeight: isSelected ? 700 : 600,
                  textAlign: 'center',
                }}
              >
                {Number(date.slice(8))}
              </span>
            )}
            {events.filter(event => event.date === date).map(block)}
          </button>
        );
      })}
    </View>
  );

  const months = monthTotals(events, year);
  const largest = Math.max(1, ...months.flatMap(m => [m.in, m.out]));
  // One bar for a month: what went through the bank, then what is planned.
  const bar = (total: number, done: number, color: string) => (
    <span style={{ display: 'flex', height: 5, gap: 1 }}>
      {done > 0 && (
        <span
          style={{
            width: `${(done / largest) * 100}%`,
            borderRadius: 2,
            backgroundColor: tint(color),
          }}
        />
      )}
      {total > done && (
        <span
          style={{
            width: `${((total - done) / largest) * 100}%`,
            boxSizing: 'border-box',
            borderRadius: 2,
            border: `1px solid ${color}`,
          }}
        />
      )}
    </span>
  );

  const yearGrid = (
    <>
      <View
        role="grid"
        aria-label={t("Mois de l'année")}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 8,
        }}
      >
        {months.map(total => {
          const current = total.month === today.slice(0, 7);
          const empty = total.in === 0 && total.out === 0;
          const balance = total.in - total.out;
          const name = capital(monthOnly.format(utc(`${total.month}-01`)));
          return (
            <button
              key={total.month}
              type="button"
              aria-label={t('Ouvrir {{month}}', {
                month: monthYear.format(utc(`${total.month}-01`)),
              })}
              onClick={() => {
                openMonth(total.month);
                setView('month');
              }}
              style={{
                minHeight: 112,
                minWidth: 0,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                borderRadius: 4,
                border: `1px solid ${current ? outColor : theme.tableBorder}`,
                backgroundColor: current
                  ? theme.tableHeaderBackground
                  : theme.tableBackground,
                color: empty ? theme.pageTextSubdued : theme.pageText,
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: current ? 700 : 600,
                  color: empty ? undefined : theme.pageTextDark,
                }}
              >
                {current ? t('{{month}} · ce mois', { month: name }) : name}
              </span>
              {empty ? (
                <span style={{ fontSize: 11 }}>
                  {`${total.month}-31` < today
                    ? t('rien à la banque')
                    : t('rien de prévu')}
                </span>
              ) : (
                <>
                  <span style={{ fontSize: 11, color: inColor }}>
                    {`+ ${rounded.format(total.in / 100)}`}
                  </span>
                  {bar(total.in, total.doneIn, inColor)}
                  <span style={{ fontSize: 11, color: outColor }}>
                    {`− ${rounded.format(total.out / 100)}`}
                  </span>
                  {bar(total.out, total.doneOut, outColor)}
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: balance >= 0 ? inColor : theme.errorText,
                    }}
                  >
                    {`${balance >= 0 ? '+' : '−'} ${rounded.format(Math.abs(balance) / 100)}`}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </View>
      {legend}
      <Text
        style={{
          fontSize: 13,
          color: theme.pageTextSubdued,
          padding: '0 4px',
        }}
      >
        {t(
          "Touchez un mois pour l'ouvrir. Les prévisions vont jusqu'au {{date}} ; au-delà, seul ce qui est passé à la banque apparaît.",
          { date: dayMonthYear.format(utc(horizon)) },
        )}
      </Text>
    </>
  );

  const modeLabel: Record<Mode, string> = {
    week: t('Semaine'),
    month: t('Mois'),
    year: t('Année'),
    list: t('Planning'),
  };
  const [before, after] =
    view === 'week'
      ? [t('Semaine précédente'), t('Semaine suivante')]
      : view === 'year'
        ? [t('Année précédente'), t('Année suivante')]
        : [t('Mois précédent'), t('Mois suivant')];
  const arrow: CSSProperties = {
    width: 44,
    height: 44,
    flexShrink: 0,
    border: 0,
    background: 'none',
    color: theme.pageText,
    cursor: 'pointer',
  };

  return (
    <MtxPage title={t('Calendrier')}>
      <View style={{ gap: 4, paddingBottom: 16, lineHeight: 1.4 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            paddingTop: 8,
            marginLeft: -8,
          }}
        >
          <button
            type="button"
            aria-label={before}
            onClick={() => step(-1)}
            style={arrow}
          >
            <SvgCheveronLeft width={22} height={22} />
          </button>
          <button
            type="button"
            aria-label={after}
            onClick={() => step(1)}
            style={arrow}
          >
            <SvgCheveronRight width={22} height={22} />
          </button>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              role="heading"
              aria-level={2}
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: theme.pageTextDark,
              }}
            >
              {view === 'week'
                ? weekTitle(week)
                : view === 'year'
                  ? year
                  : capital(monthYear.format(utc(`${month}-01`)))}
            </Text>
            <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
              <span style={{ color: inColor }}>{`+ ${money(periodIn)}`}</span>
              {' · '}
              <span style={{ color: outColor }}>{`− ${money(periodOut)}`}</span>
            </Text>
          </View>
          <button
            type="button"
            onClick={() => setSelected(today)}
            style={{ ...chipStyle(false), padding: '0 12px', flexShrink: 0 }}
          >
            <Trans>Aujourd'hui</Trans>
          </button>
        </View>

        <View
          style={{
            flexDirection: 'row',
            gap: 4,
            margin: '4px 0 8px',
            padding: 4,
            borderRadius: 6,
            border: `1px solid ${theme.tableBorder}`,
            backgroundColor: theme.pageBackground,
          }}
        >
          {MODES.map(mode => (
            <button
              key={mode}
              type="button"
              aria-pressed={view === mode}
              onClick={() => setView(mode)}
              style={{
                flex: 1,
                minHeight: 44,
                border: 0,
                borderRadius: 4,
                backgroundColor:
                  view === mode ? theme.buttonPrimaryBackground : 'transparent',
                color:
                  view === mode
                    ? theme.buttonPrimaryText
                    : theme.pageTextSubdued,
                font: 'inherit',
                fontSize: 13,
                fontWeight: view === mode ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {modeLabel[mode]}
            </button>
          ))}
        </View>

        {note && (
          <Text
            role="status"
            style={{
              fontSize: 12,
              color: theme.pageTextSubdued,
              padding: '0 4px 4px',
            }}
          >
            {note}
          </Text>
        )}

        {view === 'week' ? (
          <>
            {weekGrid}
            {legend}
            {dayPanel}
          </>
        ) : view === 'month' ? (
          <>
            {monthGrid}
            {legend}
            {dayPanel}
          </>
        ) : view === 'year' ? (
          yearGrid
        ) : (
          list
        )}
      </View>
    </MtxPage>
  );
}
